import { SettingsHistory } from "./SettingsHistory";
import { AccessSettings } from "./AccessSettings";
import React, { useEffect, useState, useRef } from "react";
import { request, Settings, Rule, TRIGGER_LABELS } from "./api";
export function SettingsView() {
  const lastWrite = useRef<{ json: string; key: string } | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [reason, setReason] = useState("");
  useEffect(() => {
    request<Settings>("/settings")
      .then(setSettings)
      .catch((e) => setError(e.message));
  }, []);
  function update(index: number, patch: Partial<Rule>) {
    setSettings(
      (s) =>
        s && {
          ...s,
          rules: s.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
        },
    );
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const fingerprint = JSON.stringify({ settings, reason });
      if (lastWrite.current?.json !== fingerprint)
        lastWrite.current = { json: fingerprint, key: crypto.randomUUID() };
      const result = await request<Settings>("/settings/versions", {
        expectedVersion: settings.version,
        idempotencyKey: lastWrite.current.key,
        timezone: settings.timezone,
        serviceDayBoundary: settings.serviceDayBoundary,
        serviceStartDate: settings.serviceStartDate || null,
        reason,
        rules: settings.rules.map((rule) => ({ ...rule, vacantArrivalNights: rule.trigger === "turnaround" ? rule.vacantArrivalNights ?? null : null })),
      });
      setSettings(result);
      window.dispatchEvent(new Event("housekeeping-settings-changed"));
      setMessage("Rules saved and active now.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!settings)
    return (
      <p role={error ? "alert" : "status"}>{error || "Loading settings..."}</p>
    );
  return (
    <>
      <form onSubmit={save} className="pfres-section">
        <div className="pfres-section__header">
          <h2 className="pfres-section__title">Cleaning rules</h2>
          <p className="pfres-section__lede">
            Cleaning depth, lightest first. Use the arrows to change priority.
            Changes apply immediately when saved.
          </p>
        </div>

        <details className="mb-3">
          <summary>Service day settings</summary>
          <div className="row g-2 mt-2">
            <label className="col-md-4">
              Timezone
              <input
                required
                className="form-control"
                value={settings.timezone}
                onChange={(e) =>
                  setSettings({ ...settings, timezone: e.target.value })
                }
              />
            </label>
            <label className="col-md-4">
              Day boundary
              <input
                type="time"
                required
                className="form-control"
                value={settings.serviceDayBoundary}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    serviceDayBoundary: e.target.value,
                  })
                }
              />
            </label>
            <label className="col-md-4">
              Housekeeping start date
              <input
                type="date"
                className="form-control"
                value={settings.serviceStartDate ?? ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    serviceStartDate: e.target.value || null,
                  })
                }
              />
              <small className="d-block text-body-secondary">
                Nothing counts as due or overdue before this date. Set it to the
                day the team starts working from these sheets, and move it if
                the start slips.
              </small>
            </label>
          </div>
        </details>
        {[...settings.rules]
          .sort((a, b) => a.rank - b.rank)
          .map((r, position, ordered) => {
            const i = settings.rules.findIndex((rule) => rule.id === r.id);
            function move(direction: number) {
              const next = position + direction;
              if (next < 0 || next >= ordered.length) return;
              const reordered = [...ordered];
              [reordered[position], reordered[next]] = [
                reordered[next],
                reordered[position],
              ];
              setSettings({
                ...settings!,
                rules: reordered.map((rule, index) => ({
                  ...rule,
                  rank: index + 1,
                })),
              });
            }
            return (
              <fieldset
                className={`pfres-panel pfres-panel--quiet hk-rule-card ${
                  r.retired ? "hk-retired" : ""
                }`}
                key={r.id}
              >
                <legend className="visually-hidden">{r.label} rule</legend>
                <div className="hk-rule-row">
                  <div className="hk-order">
                    <button
                      type="button"
                      aria-label={`Move ${r.code} lighter`}
                      disabled={position === 0}
                      onClick={() => move(-1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${r.code} deeper`}
                      disabled={position === ordered.length - 1}
                      onClick={() => move(1)}
                    >
                      ↓
                    </button>
                  </div>
                  <input
                    type="color"
                    aria-label={`${r.code} colour`}
                    value={r.color}
                    onChange={(e) => update(i, { color: e.target.value })}
                  />
                  <label className="hk-inline-label">
                    Sheet code{" "}
                    <input
                      className="form-control"
                      aria-label={`${r.code} sheet code`}
                      required
                      value={r.code}
                      onChange={(e) => update(i, { code: e.target.value })}
                    />
                  </label>
                  <label className="hk-inline-label hk-rule-name">
                    Name{" "}
                    <input
                      className="form-control hk-rule-label"
                      aria-label={`${r.code} name`}
                      required
                      value={r.label}
                      onChange={(e) => update(i, { label: e.target.value })}
                    />
                  </label>
                  <label className="hk-inline-label">
                    Duration{" "}
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      required
                      value={r.minutes}
                      onChange={(e) =>
                        update(i, { minutes: Number(e.target.value) })
                      }
                    />{" "}
                    min
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-auto"
                    onClick={() => update(i, { retired: !r.retired })}
                  >
                    {r.retired ? "Restore" : "Remove"}
                  </button>
                </div>
                <div className="hk-rule-row mt-2">
                  <select
                    className="form-select hk-trigger"
                    aria-label={`${r.code} trigger`}
                    value={r.trigger}
                    onChange={(e) =>
                      update(i, {
                        trigger: e.target.value,
                        vacantArrivalNights: e.target.value === "turnaround" ? r.vacantArrivalNights ?? null : null,
                        intervalNights: e.target.value.endsWith("Interval")
                          ? r.intervalNights || 2
                          : null,
                      })
                    }
                  >
                    {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {r.trigger === "turnaround" && (
                    <label className="hk-inline-label">
                      Also plan on arrival when vacant N+ nights (blank = off)
                      <input
                        className="form-control"
                        type="number"
                        min={1}
                        max={365}
                        step={1}
                        value={r.vacantArrivalNights ?? ""}
                        onChange={(e) => update(i, { vacantArrivalNights: e.target.value === "" ? null : Number(e.target.value) })}
                      />
                    </label>
                  )}
                  {r.trigger.endsWith("Interval") && (
                    <label className="hk-inline-label">
                      n ={" "}
                      <input
                        className="form-control"
                        type="number"
                        min={1}
                        value={r.intervalNights ?? 2}
                        onChange={(e) =>
                          update(i, { intervalNights: Number(e.target.value) })
                        }
                      />{" "}
                      nights
                    </label>
                  )}
                </div>
                <textarea
                  className="form-control hk-rule-description mt-2"
                  aria-label={`${r.code} description`}
                  rows={2}
                  value={r.description}
                  onChange={(e) => update(i, { description: e.target.value })}
                />
                <details className="hk-rule-advanced mt-2">
                  <summary>Completion coverage</summary>
                  <div className="mt-2">
                    Completion covers:{" "}
                    {settings.rules.map((other) => (
                      <label className="me-3" key={other.id}>
                        <input
                          type="checkbox"
                          checked={r.coverageRuleIds.includes(other.id)}
                          onChange={(e) =>
                            update(i, {
                              coverageRuleIds: e.target.checked
                                ? [...r.coverageRuleIds, other.id]
                                : r.coverageRuleIds.filter(
                                    (id) => id !== other.id,
                                  ),
                            })
                          }
                        />{" "}
                        {other.code}
                      </label>
                    ))}
                  </div>
                </details>
              </fieldset>
            );
          })}
        <button
          type="button"
          className="btn btn-outline-contrast"
          onClick={() =>
            setSettings({
              ...settings,
              rules: [
                ...settings.rules,
                {
                  id: crypto.randomUUID(),
                  code: "NEW",
                  label: "New clean",
                  description: "",
                  color: "#777777",
                  minutes: 30,
                  trigger: "occupiedInterval",
                  intervalNights: 2,
                  vacantArrivalNights: null,
                  rank: Math.max(0, ...settings.rules.map((r) => r.rank)) + 1,
                  coverageRuleIds: [],
                  retired: false,
                },
              ],
            })
          }
        >
          Add rule
        </button>
        <label className="d-block my-3">
          Change reason
          <textarea
            required
            maxLength={1000}
            className="form-control"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        {error && (
          <p className="text-danger" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="text-success">
            {message}
          </p>
        )}
        <button disabled={busy} className="btn btn-primary">
          {busy ? "Saving..." : "Apply rules"}
        </button>
      </form>
      <div>
        <AccessSettings />
        <SettingsHistory timezone={settings.timezone} />
      </div>
    </>
  );
}
